import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ReportCurrency } from "./use-reports";

export type DashboardScope = "own" | "team";

export interface DashboardSummary {
	currency: string;
	scope: DashboardScope;
	canSeeTeam: boolean;
	teamMeansAll: boolean;
	range: { from: string; to: string };
	offers: {
		count: number;
		converted: number;
		pending: number;
		lost: number;
		amount: number;
		convertedAmount: number;
		conversionRate: number | null;
	};
	orders: {
		count: number;
		amount: number;
		averageOrderValue: number;
		missingRateCount: number;
	};
	series: Array<{ bucket: string; offerCount: number; convertedCount: number }>;
	topCustomers: Array<{
		companyId: string | null;
		name: string;
		amount: number;
		orderCount: number;
	}>;
}

export interface DashboardActivitySummary {
	scope: DashboardScope;
	overdue: number;
	today: number;
	nextSevenDays: number;
	completedLast30Days: number;
}

const qs = (params: Record<string, string | undefined>) =>
	Object.entries(params)
		.filter(([, v]) => v !== undefined && v !== "")
		.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
		.join("&");

export function useDashboardSummary(params: {
	from: string;
	to: string;
	currency: ReportCurrency;
	scope: DashboardScope | null;
	enabled?: boolean;
}) {
	const { enabled = true, ...query } = params;
	return useQuery({
		queryKey: ["dashboard", "summary", query],
		queryFn: () =>
			api.get<DashboardSummary>(
				`/api/dashboard/summary?${qs({ ...query, scope: query.scope ?? undefined })}`,
			),
		enabled,
	});
}

export function useDashboardActivities(params: {
	scope: DashboardScope | null;
	enabled?: boolean;
}) {
	const { enabled = true, scope } = params;
	return useQuery({
		queryKey: ["dashboard", "activities", scope],
		queryFn: () =>
			api.get<DashboardActivitySummary>(
				`/api/dashboard/activities?${qs({ scope: scope ?? undefined })}`,
			),
		enabled,
	});
}
